<template>
  <BittsDrawer
    :visible="visible"
    primary-button-text="Send"
    :has-heading-padding="false"
    :mask="false"
    content-classes="bg-neutral-background-weak"
    footer-classes="bg-neutral-background-weak"
    header-classes="bg-neutral-background-weak p-0"
    header-margin="mt-[41px]"
    title=""
    width="400px"
    :is-saving-disabled="isChatResponding"
    class="ai-chat-drawer"
    @action:primary="sendMessage"
    @closed="emit('ai-chat-closed')"
  >
    <template #content>
      <div
        v-if="
          !receivedMessagesInOrder?.length && !nextMessage && !isChatResponding
        "
        class="flex flex-col items-center justify-center h-full"
      >
        <FontAwesomeIcon
          :icon="['far', 'sparkles']"
          class="text-beta-text mb-20"
          style="width: 30px; height: 30px"
        />
        <div class="flex items-center gap-8 mb-20">
          <span class="ai-chat__title ai-chat__gradient"
            >Crossbeam AI Chat</span
          >
          <BittsTag color="beta" size="x-small" text="Beta" />
        </div>
        <span class="text-neutral-text-weak mb-8">Suggested prompts:</span>
        <div class="flex flex-col items-center gap-16 max-w-full">
          <BittsTag
            v-for="prompt in suggestedPrompts"
            color="white"
            :text="prompt"
            :key="prompt"
            class="ai-chat-drawer__suggested-prompt"
            @click="() => sendSuggestedPrompt(prompt)"
          />
        </div>
      </div>
      <div
        v-else
        class="flex flex-col h-full p-16 w-full bg-neutral-background-weak"
      >
        <div
          v-for="(message, index) in receivedMessagesInOrder"
          :key="index"
          :data-testid="`chat-message-${message.role}`"
          :class="{
            'ai-chat-drawer__message': !message.errorCode,
            [message.role]: !message.errorCode,
            'opacity-40': messageCausedError(index),
          }"
        >
          <div v-if="!message.errorCode">
            <div class="flex items-center gap-8">
              <FontAwesomeIcon
                v-if="message.role === 'user' && messageCausedError(index)"
                data-testid="message-error-icon"
                :icon="['fak', 'warning']"
                class="text-secondary-text"
                style="width: 16px; height: 16px"
              />
              <div v-html="message.content" class="ai-chat-drawer__html" />
            </div>
            <FeedbackControls
              v-if="message.role === 'bot'"
              :message="message"
              :base-itly-body="baseItlyBody"
              :chat-session-id="chatSessionId"
              :messages-with-feedback="messagesWithFeedback"
              @feedback-added="
                (feedback: MessageFeedback) =>
                  messagesWithFeedback.push(feedback)
              "
            />
          </div>
        </div>
        <BittsCallout
          v-if="errorInfo"
          :subtitle="errorInfo.message"
          type="warning"
          class="mb-10"
          size="x-small"
        >
          <template #bottom-item>
            <div class="flex justify-between items-center">
              <BittsButton
                :left-icon="['fak', inTimeout ? 'loading' : 'refresh']"
                :disabled="inTimeout"
                :text="inTimeout ? errorInfo.timeoutText : errorInfo.action"
                type="primary"
                size="x-small"
                class="self-start mt-12"
                :class="{
                  'retry-button-timeout': inTimeout,
                }"
                @click="errorInfo.onClick"
              />
            </div>
          </template>
        </BittsCallout>
        <div v-if="nextMessage" class="bot ai-chat-drawer__message">
          <div v-html="formattedNextMessage" class="ai-chat-drawer__html" />
        </div>
        <div
          v-if="isChatResponding && !hasReceivedFirstResponse"
          class="flex items-center gap-5"
        >
          <FontAwesomeIcon
            :icon="['fak', 'loading']"
            class="animate-spin text-neutral-text-placeholder"
            style="width: 20px; height: 20px"
          />
          <b>Working...</b>
        </div>
      </div>
    </template>
    <template #footer>
      <div class="w-full flex flex-col gap-16">
        <div class="flex items-center gap-8">
          <BittsSwitch v-model="sendContext" />
          <b class="text-neutral-text">Include page context</b>
          <BittsTooltip trigger="hover" placement="right">
            <FontAwesomeIcon
              :icon="['fak', 'question-circle']"
              class="text-neutral-text-placeholder"
              style="width: 16px; height: 16px"
            />
            <template #title>
              Share page context with Crossbeam AI to improve the relevance of
              its answer.
            </template>
          </BittsTooltip>
        </div>
        <BittsInput
          ref="chatInput"
          data-testid="ai-chat-input"
          v-model="messageToSend"
          :disabled="inTimeout"
          name="AI Chat Input"
          :focus="visible"
          :placeholder="
            isChatResponding
              ? 'Generating response...'
              : 'Type your message here...'
          "
          @keyup.enter="handleEnter"
          class="w-full mt-auto"
          rows="3"
        >
          <template #suffix>
            <BittsButton
              data-testid="ai-chat-send-button"
              :disabled="inTimeout || waitingOnResponse"
              type="primary"
              class="ai-chat__send"
              size="medium"
              :center-icon="
                isChatResponding && !waitingOnResponse
                  ? ['fas', 'stop']
                  : ['fak', 'arrow-up']
              "
              @click.prevent="onSendOrCancel"
            />
          </template>
        </BittsInput>
      </div>
    </template>
  </BittsDrawer>
</template>
<script setup lang="ts">
import {
  BittsButton,
  BittsCallout,
  BittsDrawer,
  BittsInput,
  BittsSwitch,
  BittsTag,
  BittsTooltip,
} from '@crossbeam/bitts';

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';

import { MessageFeedback } from '@/composables/mcp/useAIChatState';
import { useMessageBuilder } from '@/composables/mcp/useMessageBuilder';
import appConfig from '@/config';

import FeedbackControls from './FeedbackControls.vue';

const { visible = false } = defineProps<{
  visible?: boolean;
}>();

const emit = defineEmits<(e: 'ai-chat-closed') => void>();

const {
  chatSessionId,
  receivedMessagesInOrder,
  formattedNextMessage,
  isChatResponding,
  hasReceivedFirstResponse,
  messageToSend,
  nextMessage,
  suggestedPrompts,
  sendMessage,
  sendContext,
  sendSuggestedPrompt,
  wsStore,
  baseItlyBody,
  iteratively,
  errorInfo,
  inTimeout,
  messagesWithFeedback,
  cancelMessage,
  waitingOnResponse,
} = useMessageBuilder();

onMounted(() => {
  wsStore?.connect();
});

function scrollToBottom() {
  nextTick(() => {
    const drawerBody = document.querySelector('.ant-drawer-body');
    if (drawerBody) {
      drawerBody.scrollTop = drawerBody.scrollHeight;
    }
  });
}

watch(
  () => nextMessage?.value,
  (newVal) => {
    if (newVal) scrollToBottom();
  },
);

const chatInput = ref<InstanceType<typeof BittsInput> | null>(null);

function messageCausedError(idx: number) {
  if (!receivedMessagesInOrder?.value?.length) return false;
  return receivedMessagesInOrder?.value[idx + 1]?.errorCode;
}

function initializeDrawer() {
  // Use a small delay to ensure the drawer and input are fully rendered
  setTimeout(() => {
    if (visible) {
      scrollToBottom();
      chatInput.value?.$el.querySelector('input')?.focus();
    }
  }, 100);
}

watch(
  () => visible,
  (newVal) => {
    if (newVal) initializeDrawer();
  },
  { immediate: true },
);

const router = useRouter();
async function handleLinkClick(event: MouseEvent) {
  const target = event.target as HTMLElement;
  const link = target.closest('a');

  if (link) {
    event.preventDefault();
    const href = link.getAttribute('href');
    if (href) {
      const isInternalRoute = href.includes(appConfig.host);
      if (isInternalRoute) {
        const url = new URL(href);
        await router.replace(`${url.pathname}${url.search}&from_ai_chat=true`);
        emit('ai-chat-closed');
      } else {
        window.open(href, '_blank');
      }
      iteratively.aiChatAction({
        ...baseItlyBody.value,
        type: 'link_click',
        metadata: {
          link: href,
        },
      } as never);
    }
  }
}

function onSendOrCancel() {
  if (!isChatResponding.value) onSend();
  else cancelMessage();
}
function onSend() {
  sendMessage();
  scrollToBottom();
}

function handleEnter(event: KeyboardEvent) {
  if (!isChatResponding.value) {
    onSend();
  } else {
    event.preventDefault();
  }
}

watch(
  [
    () => receivedMessagesInOrder?.value?.map((m) => m.content),
    () => formattedNextMessage?.value,
  ],
  ([messages, formatted]) => {
    if (messages?.length || formatted) {
      nextTick(() => {
        const htmlContainers: HTMLElement[] = Array.from(
          document.querySelectorAll('.ai-chat-drawer__html'),
        );
        htmlContainers.forEach((container) => {
          container.removeEventListener('click', handleLinkClick);
          container.addEventListener('click', handleLinkClick);
        });
      });
    }
  },
  { deep: true },
);

onBeforeUnmount(() => {
  const htmlContainers: HTMLElement[] = Array.from(
    document.querySelectorAll('.ai-chat-drawer__html'),
  );
  htmlContainers.forEach((container) => {
    container.removeEventListener('click', handleLinkClick);
  });
});
</script>

<style lang="pcss" scoped>
.retry-button-timeout {
  :deep(.c-bitts-btn__left-icon) {
    @apply animate-spin;
    transform-origin: center center;
    transform-box: fill-box;
  }
}
:deep(.bitts-callout) {
  svg.fa-information-circle {
    @apply hidden;
  }
}
.ai-chat-drawer__html {
  @apply flex flex-col gap-16;
  :deep(a) {
    @apply text-secondary-text font-bold;
    &:hover {
      @apply text-secondary-border;
    }
  }
  :deep(strong) {
    @apply font-bold text-neutral-text-strong;
  }
  :deep(hr) {
    @apply border-neutral-border mx-24;
  }
  :deep(h1) {
    @apply text-m font-bold;
  }
  :deep(h2) {
    @apply font-bold;
  }
  :deep(h3) {
    @apply font-bold;
  }
  :deep(ol) {
    @apply list-decimal pl-16;
  }
  :deep(ul) {
    @apply list-disc pl-16;
  }
  :deep(p),
  :deep(li),
  :deep(pre) {
    @apply max-w-full break-words whitespace-normal;
  }

  :deep(p),
  :deep(li) {
    @apply text-neutral-text my-4;
  }
}
.ai-chat-drawer__suggested-prompt {
  @apply cursor-pointer max-w-full text-wrap mx-48;
  :deep(.ant-tag) {
    @apply text-wrap text-center;
  }
}
.ai-chat-drawer__message {
  @apply mb-24 py-4 px-12 rounded-8 w-auto max-w-full break-words whitespace-normal;
  &.user {
    @apply self-end text-right bg-secondary-background-weak;
  }
  &.bot {
    @apply bg-neutral-background;
  }
}
.ai-chat__send.c-bitts-btn--fill:not(
    .active
  ).c-bitts-btn-primary.c-bitts-btn--fill {
  @apply rounded-bts-xxl;
  background: linear-gradient(
    95deg,
    #1c2082 3.05%,
    rgba(94, 29, 131, 0.75) 68.57%,
    rgba(135, 22, 107, 0.75) 95.34%
  );
}
.ai-chat__title {
  @apply text-xl font-bold;
}

.ai-chat__gradient {
  background: var(
    --AI-Gradient,
    linear-gradient(
      95deg,
      #1c2082 3.05%,
      rgba(94, 29, 131, 0.75) 68.57%,
      rgba(135, 22, 107, 0.75) 95.34%
    )
  );
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

@keyframes spin-in-place {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.spin-in-place {
  animation: spin-in-place 1s linear infinite;
  transform-origin: center center;
  display: inline-block;
}
</style>
